import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch, useSelector } from 'react-redux';
import { Grid, Typography } from '@mui/material';

import useAuthentication from 'base/auth/hooks/use-authentication';
import ForgotPasswordFormValidation from 'pages/forgot-password/validation/forgot-password-form-validation';
import { GuestLayout } from 'components/templates';
import logo from 'assets/images/slocoach-logo.svg';
import { selectAccessToken, selectLoader } from 'base/auth/selectors';
import { authSliceActions } from 'base/auth/slice';
import { useConfirmation } from 'base/common';
import ForgotPasswordFormView from '../forgot-password-form';

const ForgotPasswordView = () => {
  useAuthentication();
  const navigate = useNavigate();
  const methods = useForm({
    defaultValues: {
      name: '',
    },
    resolver: yupResolver(ForgotPasswordFormValidation),
    shouldFocusError: true,
  });
  const dispatch = useDispatch();
  const loading = useSelector(selectLoader);
  const token = useSelector(selectAccessToken);
  const confirm = useConfirmation();

  useEffect(() => {
    if (token) navigate('/');
  }, [token, navigate]);

  const handleSubmitFrom: SubmitHandler<any> = useCallback(
    async (data) => {
      try {
        await new Promise((resolve, reject) => {
          dispatch(authSliceActions.getCode({ ...data, resolve, reject }));
        });
      } catch (error) {
        console.log('-- forget password get code error --', error);
      }
      confirm({
        variant: 'info',
        icon: 'success',
        message: 'Your account password reset link has been sent to your email.',
      }).then(() => {
        window.location.href = '/login';
      });
    },
    [dispatch],
  );

  const formWrapperStyles = { width: { xs: '100%', md: '600px' } };

  return (
    <GuestLayout loading={loading}>
      <Grid
        container
        direction="row"
        justifyContent="center"
        alignItems="center"
        className="text-center py-4"
      >
        <Grid item sx={formWrapperStyles}>
          <Typography variant="h2" sx={{ mb: 6 }}>Reset your password</Typography>
          <FormProvider {...methods}>
            <ForgotPasswordFormView onSubmit={handleSubmitFrom} />
          </FormProvider>
          <div className="text-center mx-0 mx-lg-4">
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
              Enter your username or email address and we will
              send you instructions on how to create a new password.
            </Typography>
            <img src={logo} alt="Slo-Coach" />
          </div>
        </Grid>
      </Grid>
    </GuestLayout>
  );
};

export default ForgotPasswordView;
